// Sessão 5: Sub-board de uma esteira específica.
// URL: /esteira/:esteira (1|2|3)
// Alicerce Onda 1 (24/06): etapas lidas do backend (tabela configurável) em vez de SUB_STATUS_CONFIG.
// Drag-and-drop entre colunas chama PATCH /sub-status (mesma macro-esteira) ou
// PATCH /esteira-interna (cruza ENTRADA↔CREDITO) no backend.
//
// SPEC_LAYOUT_UX_V1 (E1, 06/07/2026) - o bucket "Crédito" (esteira=1) mescla
// visualmente 2 valores de enum do Prisma: ENTRADA ("Cadastro", 1ª fase) e
// CREDITO (análise bancária + "Seleção de Banco", etapa final). Decisão do
// Emerson: preservar o enum (não absorver/renomear) - o board concatena as
// colunas dos dois segmentos e traduz posição↔(macroFase, subStatus real).

import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import SubStatusColumn from '../components/SubStatusColumn';
import DivergenciasMini from '../components/DivergenciasMini';
import OperacaoDetalheSheet from '../components/OperacaoDetalheSheet';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import type { Operacao, Esteira, EsteiraEnum, DivergenciaConector } from '../types';
import { ESTEIRAS_CONFIG } from '../types';
import { operacoesApi, etapasSubEsteiraApi, divergenciasApi, type EtapaSubEsteira } from '../services/api';

// Record<subStatusNum combinado, Operacao[]>
type ColunasSubStatus = Record<number, Operacao[]>;

// Um "segmento" é uma macro-fase do enum Prisma com suas etapas - o bucket
// "Crédito" tem 2 segmentos (ENTRADA + CREDITO); Jurídico/Registro têm 1 só.
interface Segmento {
  macroFase: EsteiraEnum;
  etapas: EtapaSubEsteira[];
}

// Cores do breadcrumb/header por esteira (bucket visual 1-3)
const COR_BADGE: Record<number, string> = {
  0: 'bg-sky-100 text-sky-700 border-sky-200',
  1: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  2: 'bg-purple-100 text-purple-700 border-purple-200',
  3: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

// Bucket visual (URL param) → segmentos de macro-fase do enum Prisma, na
// ordem em que aparecem no board combinado.
const ESTEIRA_PARA_MACROFASES: Record<number, EsteiraEnum[]> = {
  0: ['COMERCIAL'],
  1: ['ENTRADA', 'CREDITO'],
  2: ['OPERACIONAL'],
  3: ['REGISTRO'],
};

function agruparPorSubStatusCombinado(ops: Operacao[], segmentos: Segmento[]): ColunasSubStatus {
  const total = segmentos.reduce((acc, s) => acc + s.etapas.length, 0);
  const cols: ColunasSubStatus = {};
  for (let i = 1; i <= total; i++) cols[i] = [];

  // Guarda contra seed de etapas ausente (ex.: banco novo): sem etapas não há
  // colunas a agrupar - retornar vazio em vez de estourar `.push` em undefined.
  if (total === 0) return cols;

  const offsets = new Map<EsteiraEnum, number>();
  let acc = 0;
  for (const s of segmentos) { offsets.set(s.macroFase, acc); acc += s.etapas.length; }

  ops.forEach((op) => {
    const offset = offsets.get(op.esteiraAtualEnum) ?? 0;
    const combinado = offset + op.subStatus;
    const ss = combinado >= 1 && combinado <= total ? combinado : 1;
    cols[ss].push(op);
  });
  return cols;
}

// Traduz a posição combinada (1..total) de volta para (macroFase, subStatus real
// dentro daquela macro-fase) - necessário para PATCH /esteira-interna, que exige
// os dois valores explícitos (não reseta para a etapa 1 como PATCH /esteira).
function resolverDestino(combinado: number, segmentos: Segmento[]): { macroFase: EsteiraEnum; subStatus: number } {
  let acc = 0;
  for (const s of segmentos) {
    if (combinado <= acc + s.etapas.length) return { macroFase: s.macroFase, subStatus: combinado - acc };
    acc += s.etapas.length;
  }
  const ultimo = segmentos[segmentos.length - 1];
  return { macroFase: ultimo.macroFase, subStatus: ultimo.etapas.length };
}

export default function SubBoard() {
  const { esteira: esteiraParam } = useParams<{ esteira: string }>();

  const esteira = Number(esteiraParam) as Esteira;
  const esteiraValida = [0, 1, 2, 3].includes(esteira);

  const config = esteiraValida ? ESTEIRAS_CONFIG[esteira] : null;

  const [segmentos, setSegmentos]                     = useState<Segmento[]>([]);
  const [colunas, setColunas]                         = useState<ColunasSubStatus>({});
  const [carregando, setCarregando]                   = useState(true);
  const [erro, setErro]                               = useState<string | null>(null);

  // UI pré-demo 05/07/2026, PASSO 4 - divergências PENDENTE desta esteira (todas as
  // operações), mesmo endpoint da fila geral (/divergencias?esteira=N), sem tocar no
  // fluxo de aprovação. Fila geral (topo, badge "Divergências 9+") permanece intocada.
  const [divergenciasEsteira, setDivergenciasEsteira] = useState<DivergenciaConector[]>([]);
  const [drawerDivergenciasAberto, setDrawerDivergenciasAberto] = useState(false);
  const [opDetalheId, setOpDetalheId] = useState<string | null>(null);

  const carregarDivergenciasEsteira = useCallback(async () => {
    if (!esteiraValida) return;
    try {
      const itens = await divergenciasApi.listar({ esteira, status: 'PENDENTE' });
      setDivergenciasEsteira(itens);
    } catch {
      // contador só fica em branco - não bloqueia o sub-board
    }
  }, [esteira, esteiraValida]);

  useEffect(() => { carregarDivergenciasEsteira(); }, [carregarDivergenciasEsteira]);

  const removerDivergenciaEsteira = (id: string) =>
    setDivergenciasEsteira((prev) => prev.filter((d) => d.id !== id));


  const carregarSubBoard = useCallback(async () => {
    if (!esteiraValida) return;
    setCarregando(true);
    setErro(null);
    try {
      const macroFases = ESTEIRA_PARA_MACROFASES[esteira];
      const [ops, ...etapasPorFase] = await Promise.all([
        operacoesApi.getPorEsteira(esteira),
        ...macroFases.map((mf) => etapasSubEsteiraApi.listar(mf)),
      ]);
      const novosSegmentos: Segmento[] = macroFases.map((mf, i) => ({ macroFase: mf, etapas: etapasPorFase[i] }));
      setSegmentos(novosSegmentos);
      setColunas(agruparPorSubStatusCombinado(ops, novosSegmentos));
    } catch (e: any) {
      const msg = e?.response?.data?.erro ?? e?.message ?? 'Erro desconhecido';
      setErro(`Erro ao carregar operações: ${msg}`);
    } finally {
      setCarregando(false);
    }
  }, [esteira, esteiraValida]);

  useEffect(() => { carregarSubBoard(); }, [carregarSubBoard]);

  const onDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // droppableId format: "sub-N" (N = 1-based posição combinada)
    const srcSS = Number(source.droppableId.replace('sub-', ''));
    const dstSS = Number(destination.droppableId.replace('sub-', ''));

    const destino = resolverDestino(dstSS, segmentos);

    // Optimistic update
    setColunas((prev) => {
      const next = { ...prev };
      const srcList = [...(prev[srcSS] ?? [])];
      const [moved] = srcList.splice(source.index, 1);
      const dstList = srcSS === dstSS ? srcList : [...(prev[dstSS] ?? [])];
      dstList.splice(destination.index, 0, {
        ...moved,
        subStatus: destino.subStatus,
        esteiraAtualEnum: destino.macroFase,
      });
      return { ...next, [srcSS]: srcList, [dstSS]: dstList };
    });

    if (srcSS === dstSS) return;

    try {
      if (segmentos.length > 1) {
        // Bucket mesclado ("Crédito") - sempre usa esteira-interna, que aceita
        // macroFase+subStatus explícitos (cobre mover dentro do mesmo lado e
        // cruzar ENTRADA↔CREDITO com a mesma chamada).
        await operacoesApi.moverEsteiraInterna(draggableId, destino.macroFase as 'ENTRADA' | 'CREDITO', destino.subStatus);
      } else {
        const nomeAnterior = segmentos[0]?.etapas[srcSS - 1]?.label;
        const nomeNovo     = segmentos[0]?.etapas[dstSS - 1]?.label;
        await operacoesApi.moverSubStatus(draggableId, dstSS, nomeAnterior, nomeNovo);
      }
    } catch (err: any) {
      const motivo: string = err?.response?.data?.erro ?? err?.message ?? 'Erro ao mover operação.';
      toast.error('Movimento bloqueado: ' + motivo);
      carregarSubBoard();
    }
  };

  // Clicar no card abre o detalhe num modal lateral (~56%), sem sair do sub-board.
  const abrirModal = (op: Operacao) => {
    setOpDetalheId(op.id);
  };

  const totalOps    = Object.values(colunas).flat().length;
  const totalAtivas = Object.values(colunas).flat().filter((op) => op.status === 'ATIVA').length;
  const etapasFlat  = segmentos.flatMap((s) => s.etapas);

  if (!esteiraValida) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">Esteira inválida.</p>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-100 flex flex-col">
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="px-4 py-3 flex items-center justify-between gap-3 bg-white border-b border-slate-200">
          <div className="flex items-center gap-3">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold px-2 py-1 rounded-md border ${COR_BADGE[esteira]}`}>
                Esteira {esteira}
              </span>
              <span className="font-semibold text-slate-800 text-sm">{config!.nome}</span>
              <span className="text-slate-400 text-xs">{config!.responsavel}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {carregando && (
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            )}
            <p className="text-xs text-slate-500">
              {carregando ? 'Carregando…' : `${totalOps} ops · ${totalAtivas} ativas`}
            </p>
            <button
              type="button"
              onClick={() => setDrawerDivergenciasAberto(true)}
              className="text-xs font-medium px-2 py-1 rounded-lg text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors"
            >
              Divergências: {divergenciasEsteira.length}
            </button>
            <button
              onClick={carregarSubBoard}
              disabled={carregando}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
            >
              Atualizar
            </button>
          </div>
        </div>

        {erro && (
          <div className="mx-4 mt-3 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg">
            {erro}
          </div>
        )}

        {/* Sub-board */}
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex-1 overflow-x-auto overflow-y-hidden">
            <div className="flex gap-3 p-4 h-full min-w-max">
              {etapasFlat.map((etapa, idx) => {
                const subStatusNum = idx + 1;
                return (
                  <SubStatusColumn
                    key={etapa.id}
                    esteira={esteira}
                    subStatusNum={subStatusNum}
                    total={etapasFlat.length}
                    label={etapa.label}
                    operacoes={colunas[subStatusNum] ?? []}
                    onCardClick={abrirModal}
                  />
                );
              })}
            </div>
          </div>
        </DragDropContext>
      </main>

      {/* Painel de divergências da esteira - UI pré-demo 05/07/2026, PASSO 4 */}
      <Sheet open={drawerDivergenciasAberto} onOpenChange={setDrawerDivergenciasAberto}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Divergências - Esteira {esteira}</SheetTitle>
          </SheetHeader>
          <DivergenciasMini
            divergencias={divergenciasEsteira}
            onResolvido={removerDivergenciaEsteira}
            vazio="Nenhuma divergência pendente nesta esteira."
            mostrarOperacao
          />
        </SheetContent>
      </Sheet>

      <OperacaoDetalheSheet
        operacaoId={opDetalheId}
        onClose={() => setOpDetalheId(null)}
        onUpdated={() => carregarSubBoard()}
      />
    </div>
  );
}
